import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useField, useFormikContext } from 'formik';
import cx from 'classnames';

import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CloseIcon from '@material-ui/icons/Close';

import './textInput.scss';
import { IconButton } from '@material-ui/core';

const useInputStyles = makeStyles({
  root: {
    background: 'rgba(0, 0, 0, 0.04)',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    marginTop: '0 !important',
    paddingLeft: 5,
    paddingRight: 5,
  },
});

const useLabelStyles = makeStyles({
  root: {
    display: 'inline-block',
  },
});

const useStyles = makeStyles(theme => ({
  helperText: {
    fontSize: 12,
    color: theme.palette.text.hint,
    fontStyle: 'italic',
  },
}));

const TextInput = ({
  name,
  type,
  placeholder,
  required,
  label,
  hidden,
  inputProps,
  className = '',
  additionalStyles = {},
  disabled,
  readOnly,
  autoFocus,
  helperText,
  endAdornment,
  startAdornment,
  onClear,
}) => {
  const classes = useStyles();
  const inputStyles = useInputStyles();
  const labelStyles = useLabelStyles();
  const [field, meta] = useField(name);
  const { error, touched } = meta;
  const { submitCount } = useFormikContext();

  const hasError = (error && touched) || (submitCount && error) ? true : false;

  // MUI Input does not honor a top-level `readOnly` — it must live on the native input via inputProps.
  const mergedInputProps = {
    ...inputProps,
    ...(readOnly ? { readOnly: true } : {}),
  };

  return (
    <div
      className={cx(`text-input ${className}`, {
        'd-none': hidden,
      })}
    >
      {label && (
        <Box className="mb-1">
          <InputLabel
            variant="standard"
            disableAnimation
            shrink={false}
            classes={labelStyles}
            error={hasError}
            className={additionalStyles.label}
            required={required}
            htmlFor={field.name}
          >
            {label}
          </InputLabel>
          {helperText && <Typography className={classes.helperText}> {helperText}</Typography>}
        </Box>
      )}
      <Input
        {...field}
        type={type}
        placeholder={placeholder}
        error={hasError}
        classes={inputStyles}
        className={additionalStyles.input}
        fullWidth
        id={field.name}
        inputProps={mergedInputProps}
        disabled={disabled}
        autoFocus={autoFocus}
        startAdornment={startAdornment}
        endAdornment={
          onClear ? (
            <IconButton onClick={onClear} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : (
            endAdornment
          )
        }
      />
      {hasError && error.length > 1 ? ( // check length in case this field is part of a FieldArray
        <Box component="span" color="error.main">
          {error}
        </Box>
      ) : null}
    </div>
  );
};

TextInput.defaultProps = {
  type: 'text',
  endAdornment: null,
  startAdornment: null,
};

TextInput.propTypes = {
  additionalStyles: PropTypes.shape({
    label: PropTypes.string,
    input: PropTypes.string,
  }),
};

export default TextInput;
